// App_main.jsx - Fixed without extra Router
//
// This is an updated version of the main application file. It includes
// additional routing logic to support SEO-friendly filter slugs. When
// visitors (humans) navigate to routes like `/global-relations/social-relations-news`,
// the Home component parses the slug on first render, applies a locked filter
// through FilterContext, and keeps the SPA on the pretty URL. Bots will still
// be served prerendered HTML via vercel.json rewrites.

import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider, Helmet } from 'react-helmet-async';

// Import ALL context providers
import { FilterProvider } from './contexts_main/FilterContext';
import { ThemeProvider } from './contexts_main/ThemeContext';
import { MediaPlayerProvider } from './contexts_main/MediaPlayerContext';

// Eagerly loaded (always needed)
import Layout from './components_main/layout/Layout';
import ErrorBoundary from './components_main/common/ErrorBoundary';
import ActiveMediaPlayer from './components_main/media/ActiveMediaPlayer';
import EntitySlugRedirect from './components_main/routing/EntitySlugRedirect';
import FilterSlugRedirect from './components_main/routing/FilterSlugRedirect';
import CountryRelationSlugRedirect from './components_main/routing/CountryRelationSlugRedirect';
import LiveNewsGlobeRouteShell from './components_main/liveNewsGlobe/LiveNewsGlobeRouteShell';

import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Lazy-loaded pages (code-split into separate chunks)
const Home = lazy(() => import('./pages_main/Home'));
const CountryComparison = lazy(() => import('./pages_main/CountryComparison'));
const EntityView = lazy(() => import('./pages_main/EntityView'));
const CompareSlugRouter = lazy(() => import('./components_main/routing/CompareSlugRouter'));
const WorldMapRedirect = lazy(() => import('./pages_main/WorldMapRedirect'));
const Trade = lazy(() => import('./pages_main/Trade'));
const Feeds = lazy(() => import('./pages_main/Feeds'));
const News = lazy(() => import('./pages_main/News'));
const Podcasts = lazy(() => import('./pages_main/Podcasts'));
const Streams = lazy(() => import('./pages_main/Streams'));
const Tariffs = lazy(() => import('./pages_main/Tariffs'));
const Sanctions = lazy(() => import('./pages_main/Sanctions'));
const SystemStatus = lazy(() => import('./pages_main/SystemStatus'));
const NotFound = lazy(() => import('./pages_main/NotFound'));
const EmbeddedMap = lazy(() => import('./components_main/embed/EmbeddedMap'));
const EmbeddedNewsCard = lazy(() => import('./components_main/embed/EmbeddedNewsCard'));
const LiveNewsGlobePage = lazy(() => import('./pages_main/LiveNewsGlobePage'));

// if you ever want to override via env, you could use REACT_APP_BASE_URL
const BASE_URL =
  // 1) allow an explicit override…
  process.env.REACT_APP_BASE_URL
    ? process.env.REACT_APP_BASE_URL
    // 2) otherwise, if we’re in prod, lock it to your real site…
    : process.env.NODE_ENV === 'production'
      ? 'https://www.traderelationsatlas.com'
      // 3) local/dev: fall back to whatever host served us
      : window.location.origin;

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// SEO Manager Component – now renders Helmet tags (including canonical)
function SEOManager() {
  const location = useLocation();
  const metaData = getMetaForRoute(location.pathname);

  return (
    <Helmet>
      <title>{metaData.title}</title>
      <meta name="description" content={metaData.description} />
      <meta name="keywords" content={metaData.keywords} />
      <meta property="og:title" content={metaData.title} />
      <meta property="og:description" content={metaData.description} />
      <meta property="og:image" content={`${BASE_URL}/logo512.png`} />
      <meta property="og:url" content={metaData.url} />
      <meta property="og:site_name" content={metaData.siteName} />
      <meta property="og:type" content={metaData.type} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaData.title} />
      <meta name="twitter:description" content={metaData.description} />
      <meta name="twitter:image" content={`${BASE_URL}/logo512.png`} />
      {/* ← crucial: per-page canonical */}
      <link rel="canonical" href={metaData.url} />
    </Helmet>
  );
}

function RouteFallback() {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      Loading...
    </div>
  );
}



// Function to get meta data based on route
function getMetaForRoute(pathname) {
    const baseMeta = {
      siteName: 'TRA - Trade Relations Atlas',
      // use our fixed BASE_URL instead of window.location.origin
      url: `${BASE_URL}${pathname}`,
      type: 'website'
    };

  if (pathname === '/' || pathname === '/news-atlas') {
    return {
      ...baseMeta,
      title: 'TRA - Global News Atlas | Trade Relations & Geopolitics',
      description: 'Explore global trade relations, geopolitical events, and international news through our interactive atlas and comprehensive filtering.',
      keywords: 'trade relations, geopolitics, international news, global atlas, countries, organizations',
      image: 'logo192.png'
    };
  }

  if (pathname === '/world') {
    return {
      ...baseMeta,
      title: 'Interactive World Map - Global Trade Relations | TRA',
      description: 'Explore international trade relations and geopolitical events through our interactive world map.',
      keywords: 'world map, interactive map, global trade, country relations, geopolitics',
      image: 'logo192.png'
    };
  }

  if (pathname === '/trade') {
    return {
      ...baseMeta,
      title: 'Trade Atlas — Compare Global Trade Flows | TRA',
      description: 'Analyze bilateral and unilateral trade data, explore HS2/HS6 goods breakdowns, and navigate an interactive map of global trade relationships.',
      keywords: 'trade data, bilateral trade, HS2, HS6, trade flows, trade atlas',
      image: 'logo192.png'
    };
  }

  // Country pages
  if (pathname.startsWith('/countries/')) {
    const countrySlug = pathname.replace('/countries/', '');
    const countryName = formatSlugToName(countrySlug);
    
    return {
      ...baseMeta,
      title: `${countryName} - Trade Relations & Latest News | TRA`,
      description: `${countryName}'s trade relations, economic indicators, diplomatic activities, and latest news updates.`,
      keywords: `${countryName}, trade relations, country news, economic indicators, international relations`,
      image: 'logo192.png',
      // image: `/images/countries/${countrySlug}-og.jpg`,
      type: 'article'
    };
  }

  // Organization pages
  if (pathname.startsWith('/organizations/')) {
    const orgSlug = pathname.replace('/organizations/', '');
    const orgName = formatSlugToName(orgSlug);
    
    return {
      ...baseMeta,
      title: `${orgName} - International Organization News & Analysis | TRA`,
      description: `Stay updated on ${orgName}'s activities, decisions, and impact on global trade and international relations.`,
      keywords: `${orgName}, international organization, global trade, multilateral affairs`,
      image: 'logo192.png',
      // image: `/images/organizations/${orgSlug}-og.jpg`,
      type: 'article'
    };
  }

  // Region pages
  if (pathname.startsWith('/regions/')) {
    const regionSlug = pathname.replace('/regions/', '');
    const regionName = formatSlugToName(regionSlug);

    return {
      ...baseMeta,
      title: `${regionName} - Region Overview & Member Countries | TRA`,
      description: `Explore ${regionName}'s member countries, trade relations, geopolitical events, and regional dynamics.`,
      keywords: `${regionName}, region, member countries, trade bloc, international relations`,
      image: 'logo192.png',
      type: 'article'
    };
  }

  // People pages
  if (pathname.startsWith('/people/')) {
    const personSlug = pathname.replace('/people/', '');
    const personName = formatSlugToName(personSlug);
    
    return {
      ...baseMeta,
      title: `${personName} - International Leader News & Impact | TRA`,
      description: `Follow ${personName}'s involvement in international trade, diplomacy, and global affairs.`,
      keywords: `${personName}, international figure, diplomacy, global affairs`,
      image: 'logo192.png',
      // image: `/images/people/${personSlug}-og.jpg`,
      type: 'profile'
    };
  }

  // Other pages...
  if (pathname === '/news') {
    return {
      ...baseMeta,
      title: 'Global Trade & Political News | TRA',
      description: 'Latest international trade news, geopolitical developments, and global economic updates.',
      keywords: 'international news, trade news, geopolitical news',
      image: 'logo192.png'
      // image: '/images/og-image-news.jpg'
    };
  }

  if (pathname === '/feeds') {
    return {
      ...baseMeta,
      title: 'RSS Feeds & News Sources | TRA',
      description: 'Access curated RSS feeds for news, podcasts and streams about global trade and international relations.',
      keywords: 'RSS feeds, news sources, international media',
      image: 'logo192.png'
      // image: '/images/og-image-feeds.jpg'
    };
  }

  if (pathname === '/podcasts') {
    return {
      ...baseMeta,
      title: 'International Relations Podcasts | TRA',
      description: 'Listen to expert analysis and discussions on global trade and international relations.',
      keywords: 'international relations podcasts, trade podcasts',
      image: 'logo192.png'
      // image: '/images/og-image-podcasts.jpg'
    };
  }

  if (pathname === '/streams') {
    return {
      ...baseMeta,
      title: 'Live News Streams & Analysis | TRA',
      description: 'Watch live news streams and real-time analysis of global events.',
      keywords: 'live news streams, real-time analysis',
      image: 'logo192.png'
      //image: '/images/og-image-streams.jpg'
    };
  }

  if (pathname === '/status') {
    return {
      ...baseMeta,
      title: 'System Status | Trade Relations Atlas',
      description: 'Current update status for Trade Relations Atlas article, podcast and live-stream feeds.',
      keywords: 'Trade Relations Atlas status, feed updates',
      image: 'logo192.png'
    };
  }

  if (pathname === '/sanctions') {
    return {
      ...baseMeta,
      title: 'International Sanctions Database — UN, EU, UK & OFAC | TRA',
      description: 'Search and explore international sanctions lists from the UN, EU, UK (OFSI), and OFAC. Filter by source, entity type, and program.',
      keywords: 'sanctions database, OFAC, EU sanctions, UN sanctions, UK OFSI, sanctioned entities, sanctions list',
      image: 'logo192.png'
    };
  }

  if (pathname === '/tariffs') {
    return {
      ...baseMeta,
      title: 'Global Tariff Database & Trade Analysis | TRA',
      description: 'Comprehensive database of international tariffs and trade agreements.',
      keywords: 'tariffs database, trade agreements',
      image: '/images/og-image-tariffs.jpg'
    };
  }

  return {
    ...baseMeta,
    title: 'TRA - Trade Relations Atlas',
    description: 'Map entities. Track relationships. Analyze global events, trade, and data.',
    keywords: 'trade relations, international news, global analysis',
    image: 'logo192.png'
  };
}

// Function to update meta tags dynamically
function updateMetaTags(metaData) {
  document.title = metaData.title;

  const metaTags = [
    { name: 'description', content: metaData.description },
    { name: 'keywords', content: metaData.keywords },
    { property: 'og:title', content: metaData.title },
    { property: 'og:description', content: metaData.description },
    { property: 'og:image', content: `${window.location.origin}${metaData.image}` },
    { property: 'og:url', content: metaData.url },
    { property: 'og:site_name', content: metaData.siteName },
    { property: 'og:type', content: metaData.type },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaData.title },
    { name: 'twitter:description', content: metaData.description },
    { name: 'twitter:image', content: `${window.location.origin}${metaData.image}` }
  ];

  metaTags.forEach(tag => {
    const selector = tag.name ? `meta[name="${tag.name}"]` : `meta[property="${tag.property}"]`;
    let element = document.querySelector(selector);
    
    if (!element) {
      element = document.createElement('meta');
      if (tag.name) element.setAttribute('name', tag.name);
      if (tag.property) element.setAttribute('property', tag.property);
      document.head.appendChild(element);
    }
    
    element.setAttribute('content', tag.content);
  });

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', metaData.url);
}

function formatSlugToName(slug) {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1));
}

function App_main() {
  const location = useLocation();
  
  // Dispatch prerender-ready event for SSR/prerendering
  useEffect(() => {
    window.dispatchEvent(new Event('prerender-ready'));
  }, [location.pathname]);
  
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FilterProvider>
            <MediaPlayerProvider>
              {/* SEO Manager - will work because Router is elsewhere */}
              <SEOManager />
              
              <Helmet>
                <html lang="en" />
                <meta charSet="utf-8" />
                <meta name="robots" content="index, follow" />
                <meta name="author" content="Trade Relations Atlas" />
                <link rel="icon" href="/favicon.ico" />
                
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Trade Relations Atlas",
                    "description": "Comprehensive platform for tracking global trade relations, international news, and geopolitical developments",
                    "url": "https://www.traderelationsatlas.com",
                    "publisher": {
                      "@type": "Organization",
                      "name": "Trade Relations Atlas",
                      "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.traderelationsatlas.com/logo512.png"
                      }
                    }
                  })}
                </script>
              </Helmet>

              <ErrorBoundary key={location.pathname}>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                {/* Experimental standalone route; intentionally absent from nav/sitemap. */}
                <Route
                  path="/livenewsglobe"
                  element={(
                    <LiveNewsGlobeRouteShell>
                      <Suspense fallback={<RouteFallback />}>
                        <LiveNewsGlobePage />
                      </Suspense>
                    </LiveNewsGlobeRouteShell>
                  )}
                />
                <Route path="/livenewsglobe/*" element={<NotFound />} />

                {/* World - standalone entry to open atlas */}
                <Route path="/world" element={<WorldMapRedirect />} />
                
                {/* Embedded routes - no layout */}
                <Route path="/embed/map/:eventId" element={<EmbeddedMap />} />
                <Route path="/embed/newscard/:eventId" element={<EmbeddedNewsCard />} />
                
                {/* Main app routes with layout */}
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="trade" element={<Trade />} />
                  <Route path="feeds" element={<Feeds />} />
                  <Route path="news" element={<News />} />
                  <Route path="podcasts" element={<Podcasts />} />
                  <Route path="streams" element={<Streams />} />
                  <Route path="status" element={<SystemStatus />} />
                  <Route path="tariffs" element={<Tariffs />} />
                  <Route path="sanctions" element={<Sanctions />} />
                  
                  {/* Compare Countries routes */}
                  <Route path="compare" element={<CountryComparison />} />
                  <Route path="compare/:pair" element={<CountryComparison />} />
                  
                  {/* SEO compare slug router: resolves slug -> ISO and redirects to /compare?country1&country2 */}
                  <Route path="countries/compare/:pair" element={<CompareSlugRouter />} />
                  
                  {/* SEO-friendly filter routes: render Home so slug metadata can apply locked filters */}
                  <Route
                    path="global-relations/:slug"
                    element={<FilterSlugRedirect kind="relations" />} />
                  <Route
                    path="global-impact/:slug"
                    element={<FilterSlugRedirect kind="impact" />} />
                  <Route
                    path="global-interaction/:slug"
                    element={<FilterSlugRedirect kind="interaction" />} />
                  <Route
                    path="global-role/:slug"
                    element={<FilterSlugRedirect kind="role" />} />
                  <Route
                    path="global-sector/:slug"
                    element={<FilterSlugRedirect kind="sector" />} />
                  <Route
                    path="global-intensity/:slug"
                    element={<FilterSlugRedirect kind="intensity" />} />
                  <Route
                    path="global-scope/:slug"
                    element={<FilterSlugRedirect kind="scope" />} />

                  {/* SEO-friendly entity routes */}
                  <Route
                    path="countries/:countrySlug/relations-with/:otherSlug"
                    element={<CountryRelationSlugRedirect mode="bilateral" />} />
                  <Route
                    path="countries/:countrySlug/:relationSlug"
                    element={<CountryRelationSlugRedirect />} />
                  <Route path="countries/:slug" element={<EntitySlugRedirect entityType="country" />} />
                  <Route path="organizations/:slug" element={<EntitySlugRedirect entityType="organization" />} />
                  <Route path="people/:slug" element={<EntitySlugRedirect entityType="person" />} />
                  <Route path="regions/:slug" element={<EntitySlugRedirect entityType="region" />} />

                  {/* Legacy entity routes */}
                  <Route path="entity">
                    <Route path="country/:id" element={<EntityView type="country" />} />
                    <Route path="person/:id" element={<EntityView type="person" />} />
                    <Route path="organization/:id" element={<EntityView type="organization" />} />
                    <Route path="region/:id" element={<EntityView type="region" />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
                </Suspense>
              </ErrorBoundary>

              <ErrorBoundary fallback={null}>
                <ActiveMediaPlayer />
              </ErrorBoundary>

              <Analytics />
              <SpeedInsights />

            </MediaPlayerProvider>
          </FilterProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App_main;
